import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { AuditsApi, CollectingOperationStatusDto, CollectingOperationsSummaryDto } from '@buspro/web/apis';
import { FormsModule } from '@angular/forms';
import { EmptyStateComponent } from '@buspro/web/shared';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { lastValueFrom } from 'rxjs';

/** A trip is treated as stalled once nothing new has arrived for this long. */
const STALLED_AFTER_MINUTES = 30;

/**
 * Trips whose footage is still arriving — not auditable, and deliberately not openable.
 *
 * An operation stays in COLLECTING_MEDIA until every crossing has every expected channel,
 * with no timeout and no promotion with gaps, so without this panel a trip that never
 * completes is simply absent from the auditor's queue with nothing to explain why. This is
 * where a human sees what is waiting, how far along it is, which crossings are still short,
 * and — the question that actually decides what to do — whether footage is still arriving
 * or we are merely polling for it.
 *
 * No "start audit" action by design: readiness is the server's decision, and a trip leaves
 * this panel by promoting itself.
 */
@Component({
  selector: 'buspro-admin-collecting-operations-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, NzIconModule, NzToolTipModule, EmptyStateComponent],
  templateUrl: './collecting-operations-panel.component.html',
})
export class CollectingOperationsPanelComponent {
  private auditsApi = inject(AuditsApi);

  /**
   * Same 30s cadence as the ready-for-audit list beside it, so a trip that promotes leaves
   * this panel and appears there within one refresh without anyone reloading the page.
   * placeholderData keeps the previous list on screen while refetching — otherwise the
   * panel would blink empty every half minute.
   */
  collectingQuery = injectQuery(() => ({
    queryKey: ['operations-collecting-media'],
    queryFn: () => lastValueFrom(this.auditsApi.auditsControllerGetCollectingOperations()),
    refetchInterval: 30000,
    placeholderData: (previousData: CollectingOperationsSummaryDto | undefined) => previousData,
  }));

  operations = computed(() => this.collectingQuery.data()?.operations ?? []);
  total = computed(() => this.collectingQuery.data()?.total ?? 0);
  buckets = computed(() => this.collectingQuery.data()?.ageBuckets);

  /** Only a first paint gets skeletons; a refetch dims what is already there. */
  isFirstLoad = computed(() => this.collectingQuery.isPending() && !this.collectingQuery.data());

  /** Trips the operator should look at: nothing new has arrived for a while. */
  stalledCount = computed(() => this.operations().filter((op) => this.isStalled(op)).length);

  /**
   * Minutes since the newest clip for this trip. Null when nothing has ever arrived, which
   * is a different state from "arrived a while ago" and is rendered as such.
   */
  minutesSinceLastClip(op: CollectingOperationStatusDto): number | null {
    if (!op.lastClipAt) return null;
    return Math.max(0, Math.round((Date.now() - new Date(op.lastClipAt).getTime()) / 60000));
  }

  /**
   * Stalled = we are still polling but nothing has come back for a while, including the
   * case where nothing ever came back. This is the distinction between a trip that is
   * simply slow and one where somebody needs to look at the bus.
   */
  isStalled(op: CollectingOperationStatusDto): boolean {
    const since = this.minutesSinceLastClip(op);
    if (since === null) return (op.ageMinutes ?? 0) >= STALLED_AFTER_MINUTES;
    return since >= STALLED_AFTER_MINUTES;
  }

  /**
   * How the bus's telematics link should read, or null to say nothing at all.
   *
   * busOnline is deliberately tri-state: null means the provider could not tell us, which
   * is not offline. Rendering "offline" for an unknown would send someone to check a bus
   * that is fine, so an unknown shows nothing.
   */
  busLink(op: CollectingOperationStatusDto): { label: string; tone: 'success' | 'error' } | null {
    if (op.busOnline === null || op.busOnline === undefined) return null;
    if (op.busOnline) return { label: 'bus online', tone: 'success' };
    const forMinutes = op.busOfflineForMinutes;
    return {
      label: forMinutes === null ? 'bus offline' : `bus offline ${this.humanMinutes(forMinutes)}`,
      tone: 'error',
    };
  }

  /** "4h 12m", "18m" — a duration a person reads at a glance. */
  humanMinutes(total: number | null): string {
    if (total === null || total < 1) return 'just now';
    if (total < 60) return `${total}m`;
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h < 24) return m ? `${h}h ${m}m` : `${h}h`;
    const d = Math.floor(h / 24);
    return `${d}d ${h % 24}h`;
  }

  /** Plain-language reason a trip is not auditable yet. */
  waitingOn(op: CollectingOperationStatusDto): string {
    const short = op.missing.length;
    if (short === 0) return 'Finishing up';
    const channels = op.expectedChannels ?? 0;
    const crossing = short === 1 ? 'crossing' : 'crossings';
    return channels ? `${short} ${crossing} short of ${channels} channels` : `${short} ${crossing} incomplete`;
  }

  gapLabel(
    gap: { busStopName: string | null; direction: string | null; received: number },
    expected: number | null,
  ): string {
    const stop = gap.busStopName ?? 'Unknown stop';
    const dir = gap.direction === 'exit' ? 'departure' : 'arrival';
    return expected ? `${stop} ${dir} — ${gap.received} of ${expected}` : `${stop} ${dir} — ${gap.received}`;
  }

  /** Which trip's gaps are expanded. Only one at a time — this is a glance surface, not a workspace. */
  expandedId = signal<string | null>(null);
  /** The trip whose override confirmation is open, and the reason typed so far. */
  confirmingId = signal<string | null>(null);
  overrideReason = signal('');
  busy = signal<string | null>(null);
  feedback = signal<{ id: string; text: string; tone: 'success' | 'error' } | null>(null);

  toggleExpanded(op: CollectingOperationStatusDto): void {
    this.expandedId.update((id) => (id === op.operationId ? null : op.operationId));
  }

  /**
   * Whether asking the bus again can achieve anything.
   *
   * Offline is a dead end — the device cannot be reached, so the button is disabled and says
   * why. A disabled control with no explanation gets read as a broken page.
   */
  retryBlockedReason(op: CollectingOperationStatusDto): string | null {
    if (op.busOnline === false) return 'The bus is offline — it cannot be asked until it reconnects';
    // "Stuck" and "failed" are not the same thing. Across two live trips and 48 clips every gap was
    // footage still on its way, never an upload the provider had given up on — so this is the
    // common case, and saying only "nothing to re-ask for" reads like a fault. Say which it is.
    if (op.recoveryPending === 0) {
      return 'Nothing has failed — the footage is still on its way, so there is nothing to re-ask for';
    }
    return null;
  }

  /**
   * The override unlocks 20 minutes after the trip started collecting.
   *
   * A floor, not a recommendation. The panel shows what recovery is still doing beside it, so
   * "waiting is working" and "waiting is hopeless" do not read the same.
   */
  overrideUnlocked(op: CollectingOperationStatusDto): boolean {
    return (op.ageMinutes ?? 0) >= 20;
  }

  minutesUntilOverride(op: CollectingOperationStatusDto): number {
    return Math.max(0, 20 - (op.ageMinutes ?? 0));
  }

  async retryNow(op: CollectingOperationStatusDto): Promise<void> {
    this.busy.set(op.operationId);
    this.feedback.set(null);
    try {
      const res = await lastValueFrom(
        this.auditsApi.auditsControllerRetryMediaNow({ operationId: op.operationId }),
      );
      this.feedback.set({ id: op.operationId, text: res.message, tone: 'success' });
      await this.collectingQuery.refetch();
    } catch (err: unknown) {
      this.feedback.set({ id: op.operationId, text: this.errorText(err), tone: 'error' });
    } finally {
      this.busy.set(null);
    }
  }

  openConfirm(op: CollectingOperationStatusDto): void {
    this.overrideReason.set('');
    this.feedback.set(null);
    this.confirmingId.set(op.operationId);
  }

  cancelConfirm(): void {
    this.confirmingId.set(null);
    this.overrideReason.set('');
  }

  async confirmForceAudit(op: CollectingOperationStatusDto): Promise<void> {
    this.busy.set(op.operationId);
    try {
      await lastValueFrom(
        this.auditsApi.auditsControllerForceIntoAudit({
          operationId: op.operationId,
          body: { reason: this.overrideReason() },
        }),
      );
      this.confirmingId.set(null);
      this.overrideReason.set('');
      await this.collectingQuery.refetch();
    } catch (err: unknown) {
      this.feedback.set({ id: op.operationId, text: this.errorText(err), tone: 'error' });
    } finally {
      this.busy.set(null);
    }
  }

  private errorText(err: unknown): string {
    const message = (err as { error?: { message?: string | string[] } })?.error?.message;
    if (Array.isArray(message)) return message.join(', ');
    return message ?? 'Something went wrong — try again';
  }

  /** What recovery is currently doing for this trip, or null when it has nothing to say. */
  recoveryLine(op: CollectingOperationStatusDto): { label: string; tone: 'warning' | 'error' } | null {
    if (op.recoveryAbandoned > 0) {
      return { label: `recovery gave up on ${op.recoveryAbandoned} — waiting will not help`, tone: 'error' };
    }
    if (op.recoveryPending > 0) {
      const tries = op.recoveryAttempts;
      return {
        label: `re-asking the bus for ${op.recoveryPending} clip(s)${tries ? ` — try ${tries + 1}` : ''}`,
        tone: 'warning',
      };
    }
    return null;
  }

  trackById(_index: number, op: CollectingOperationStatusDto): string {
    return op.operationId;
  }
}
